import { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  Chip,
  useTheme
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function ClientPerformanceTable({ data, loading, title = 'Integration Performance' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const divider = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const panelHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#fafafa';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.05)' : '#f4f5f7';
  const tableHeaderColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const primaryText = isDark ? theme.palette.text.primary : '#0f172a';
  const secondaryText = isDark ? theme.palette.text.secondary : '#6b7280';
  const rowAlt = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const rowTop = isDark ? 'rgba(226,0,27,0.06)' : 'rgba(226,0,27,0.025)';
  const rowHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)';
  const rowBorder = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const rankTopColor = '#e2001b';
  const rankColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)';
  const barTrack = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const footerBg = isDark ? 'rgba(255,255,255,0.02)' : '#fafafa';
  const footerText = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.35)';

  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return [...data]
      .filter((client) => client.betCount > 0 || client.playerCount > 0)
      .sort((a, b) => (b.betSum || 0) - (a.betSum || 0));
  }, [data]);

  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '-';
    return `${Number(num).toFixed(2)}%`;
  };

  const getPercentColor = (percent) => {
    if (percent === null || percent === undefined) return 'default';
    if (percent > 20) return 'error';
    if (percent > 10) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Card>
        <MDBox p={3}>
          <Skeleton variant='text' width={200} height={32} />
          <Skeleton variant='rectangular' height={300} sx={{ mt: 2 }} />
        </MDBox>
      </Card>
    );
  }

  if (sortedData.length === 0) {
    return (
      <Card>
        <MDBox p={3}>
          <MDTypography variant='h6' fontWeight='medium'>
            {title}
          </MDTypography>
          <MDBox display='flex' justifyContent='center' alignItems='center' py={4}>
            <MDTypography variant='body2' color='text'>
              No integration data available for the selected period
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  }

  const maxBetSum = Math.max(...sortedData.map((c) => c.betSum || 0), 1);

  return (
    <Card
      sx={{
        borderRadius: '14px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Card panel header */}
      <MDBox
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.75,
          borderBottom: `1px solid ${divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: panelHeaderBg
        }}
      >
        <MDTypography variant='h6' fontWeight='bold' sx={{ letterSpacing: '-0.01em', fontSize: '0.95rem', color: primaryText }}>
          {title}
        </MDTypography>
        <MDBox
          sx={{
            px: 1.25,
            py: 0.35,
            borderRadius: '6px',
            background: isDark ? 'rgba(255,255,255,0.05)' : theme.palette.background.paper,
            border: `1px solid ${divider}`
          }}
        >
          <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.65rem', color: tableHeaderColor, letterSpacing: '0.04em' }}>
            {sortedData.length} {sortedData.length === 1 ? 'integration' : 'integrations'}
          </MDTypography>
        </MDBox>
      </MDBox>

      <TableContainer
        sx={{
          maxHeight: 460,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: '4px', width: '4px' },
          '&::-webkit-scrollbar-thumb': { background: isDark ? 'rgba(255,255,255,0.15)' : theme.palette.action.hover, borderRadius: '2px' }
        }}
      >
        <Table size='small' stickyHeader sx={{ width: '100%', minWidth: { xs: 480, sm: 'auto' } }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  background: tableHeaderBg,
                  color: tableHeaderColor,
                  fontWeight: 700,
                  fontSize: '0.67rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  padding: { xs: '9px 10px', sm: '9px 16px' },
                  borderBottom: `1px solid ${divider}`,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none'
                }
              }}
            >
              <TableCell sx={{ width: 28, pr: 0 }}>#</TableCell>
              <TableCell>Integration</TableCell>
              <TableCell align='right'>Players</TableCell>
              <TableCell align='right'>Bets</TableCell>
              <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Avg</TableCell>
              <TableCell align='right' sx={{ minWidth: 140 }}>Amount</TableCell>
              <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>Non-RB</TableCell>
              <TableCell align='right'>% RB</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((client, index) => {
              const sharePct = Math.round(((client.betSum || 0) / maxBetSum) * 100);
              const isTop = index === 0;
              return (
                <TableRow
                  key={client.name || index}
                  sx={{
                    background: isTop ? rowTop : index % 2 === 0 ? 'transparent' : rowAlt,
                    '@media (hover: hover)': {
                      '&:hover': { background: rowHover }
                    },
                    '& td': {
                      padding: { xs: '9px 10px', sm: '9px 16px' },
                      borderBottom: `1px solid ${rowBorder}`
                    },
                    '&:last-child td': { borderBottom: 'none' }
                  }}
                >
                  {/* Rank */}
                  <TableCell sx={{ width: 28, pr: 0 }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.65rem', color: isTop ? rankTopColor : rankColor, fontWeight: isTop ? 700 : 400 }}>
                      {index + 1}
                    </MDTypography>
                  </TableCell>

                  {/* Name */}
                  <TableCell>
                    <MDTypography
                      variant='button'
                      fontWeight={isTop ? 'bold' : 'medium'}
                      sx={{ fontSize: { xs: '0.72rem', sm: '0.78rem' }, color: primaryText, display: 'block', lineHeight: 1.3 }}
                    >
                      {client.name || 'Unknown'}
                    </MDTypography>
                  </TableCell>

                  {/* Players */}
                  <TableCell align='right'>
                    <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                      {formatNumber(client.playerCount)}
                    </MDTypography>
                  </TableCell>

                  {/* Bets */}
                  <TableCell align='right'>
                    <MDTypography variant='caption' sx={{ fontSize: '0.75rem', color: theme.palette.text.primary }}>
                      {formatNumber(client.betCount)}
                    </MDTypography>
                  </TableCell>

                  {/* Avg */}
                  <TableCell align='right' sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>
                      {formatNumber(client.betAverage, 2)}
                    </MDTypography>
                  </TableCell>

                  {/* Amount with inline share bar */}
                  <TableCell align='right' sx={{ minWidth: 140 }}>
                    <MDBox display='flex' flexDirection='column' alignItems='flex-end' gap={0.4}>
                      <MDTypography variant='caption' fontWeight='bold' sx={{ fontSize: '0.78rem', color: primaryText }}>
                        {formatNumber(client.betSum, 2)}
                      </MDTypography>
                      <MDBox sx={{ width: '100%', height: '3px', borderRadius: '2px', background: barTrack, overflow: 'hidden' }}>
                        <MDBox
                          sx={{
                            height: '100%',
                            width: `${sharePct}%`,
                            background: isTop ? '#e2001b' : 'rgba(226,0,27,0.45)',
                            borderRadius: '2px',
                            transition: 'width 0.4s ease'
                          }}
                        />
                      </MDBox>
                    </MDBox>
                  </TableCell>

                  {/* Non-RB */}
                  <TableCell align='right' sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <MDTypography variant='caption' sx={{ fontSize: '0.73rem', color: secondaryText }}>
                      {formatNumber(client.nonRolledBackBetsCount)}
                    </MDTypography>
                  </TableCell>

                  {/* % RB */}
                  <TableCell align='right'>
                    <Chip
                      label={formatPercent(client.percentRolledBackBets)}
                      size='small'
                      color={getPercentColor(client.percentRolledBackBets)}
                      sx={{ minWidth: 52, fontSize: '0.67rem', height: 19, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer */}
      <MDBox
        sx={{
          px: { xs: 2, sm: 3 },
          py: 1.25,
          borderTop: `1px solid ${divider}`,
          background: footerBg,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <MDTypography variant='caption' sx={{ fontSize: '0.68rem', color: footerText }}>
          Sorted by bet amount · Bar shows share of top integration
        </MDTypography>
      </MDBox>
    </Card>
  );
}

ClientPerformanceTable.defaultProps = {
  data: [],
  loading: false,
  title: 'Integration Performance'
};

ClientPerformanceTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      playerCount: PropTypes.number,
      betCount: PropTypes.number,
      betAverage: PropTypes.number,
      betSum: PropTypes.number,
      nonRolledBackBetsCount: PropTypes.number,
      percentRolledBackBets: PropTypes.number
    })
  ),
  loading: PropTypes.bool,
  title: PropTypes.string
};

export default ClientPerformanceTable;
